import Image from 'next/image'
import { useMemo } from 'react'
import { twMerge } from 'tailwind-merge'

import { SCREEN_SIZE, SPACE } from '@nxt-insurance/ui-kit/constants'
import { borderRadius } from '@nxt-insurance/ui-kit/styles/borderRadius'
import { bgColor, borderColor } from '@nxt-insurance/ui-kit/styles/colors'
import { marginBottom, marginTop } from '@nxt-insurance/ui-kit/styles/margin'
import { paddingX, paddingY } from '@nxt-insurance/ui-kit/styles/padding'
import type { Color } from '@nxt-insurance/ui-kit/types'

import { generateSrcSet, urlFor } from '@/cms/client'
import type { Section } from '@/cms/sections'
import { BlockContent } from '@/components/renderers/BlockContent'
import { multiColumnSection } from '@/components/renderers/MultiColumnSection'
import { MAX_CONTENT_WIDTH } from '@/constants/content'
import { useWindowWidth } from '@/hooks/useWindowWidth'
import { itemsAlignmentTailwind, justifyAlignmentTailwind } from '@/utils/style'
import type { PortableTextComponentProps } from '@portabletext/react'
import { gapX, gapY } from '@ui-kit/styles/gap'

type ExtractedSection = Extract<Section, { _type: 'website.richContent.section' }>

type SectionColumn = ExtractedSection['base']['columns'][number]

const PADDING_SIDES = SPACE.xxsmall * 2

const ColumnRenderer = (props: {
  column: SectionColumn
  sectionName: string
  maxContentWidth: number
  layoutWidth?: number
  numberOfColumns: number
  hasPriority: boolean
  backgroundColor?: Color
  contentColor?: Color
}) => {
  const { column } = props

  const windowWidth = useWindowWidth()

  const currentVariant = useMemo(() => {
    if (windowWidth >= SCREEN_SIZE.XXL && column.xxl) {
      return column.xxl
    } else if (windowWidth >= SCREEN_SIZE.XL && column.xl) {
      return column.xl
    } else if (windowWidth >= SCREEN_SIZE.L && column.lg) {
      return column.lg
    } else if (windowWidth >= SCREEN_SIZE.M && column.md) {
      return column.md
    } else if (windowWidth >= SCREEN_SIZE.S && column.sm) {
      return column.sm
    }
    return column.base
  }, [windowWidth, column])

  const backgroundImageProps = useMemo(() => {
    if (!currentVariant.backgroundImage) {
      return
    }
    const url = urlFor(currentVariant.backgroundImage).width(props.maxContentWidth * 2)
    return {
      src: url.url(),
      srcSet: generateSrcSet(url),
      loader: ({ src }: { src: string }) => src,
    }
  }, [currentVariant.backgroundImage, props.maxContentWidth])

  const maxWidth = useMemo(() => {
    if (!props.layoutWidth) {
      return Math.floor(props.maxContentWidth / props.numberOfColumns)
    }
    return Math.floor(props.maxContentWidth * (props.layoutWidth / 100))
  }, [props.maxContentWidth, props.layoutWidth, props.numberOfColumns])

  if (windowWidth === 0) {
    return null
  }

  return (
    <div
      className={twMerge(
        'relative flex flex-1 flex-col overflow-hidden border',
        currentVariant.fullHeight !== false && 'lg:h-full',
        bgColor({ color: currentVariant.backgroundColor ?? undefined }),
        itemsAlignmentTailwind[currentVariant.alignHorizontally ?? 'left'],
        justifyAlignmentTailwind[currentVariant.alignVertically ?? 'top'],
        paddingX({ space: currentVariant.horizontalPadding ?? undefined }),
        paddingY({ space: currentVariant.verticalPadding ?? undefined }),
        borderColor({ color: currentVariant.borderColor ?? 'transparent' }),
        borderRadius({
          radius: currentVariant.borderRadius ?? undefined,
        }),
        backgroundImageProps && '-my-1 -mr-2'
      )}
      style={{
        height: backgroundImageProps ? `calc(100% + ${PADDING_SIDES}px)` : '100%',
        maxWidth: backgroundImageProps ? maxWidth + PADDING_SIDES : maxWidth,
      }}
    >
      {backgroundImageProps ? (
        <Image
          {...backgroundImageProps}
          alt=""
          fill
          style={{
            objectFit: currentVariant.backgroundImage?.fit ?? undefined,
            objectPosition: currentVariant.backgroundImage?.imagePosition ?? undefined,
          }}
        />
      ) : null}
      <BlockContent
        blocks={currentVariant.content ?? []}
        align={currentVariant.alignHorizontally ?? 'left'}
        sectionName={props.sectionName}
        width={maxWidth}
        contentColor={props.contentColor}
        backgroundColor={currentVariant.backgroundColor ?? props.backgroundColor ?? undefined}
        space={currentVariant.space ?? undefined}
        hasPriority={props.hasPriority}
      />
    </div>
  )
}

export const SectionRenderer = (props: {
  node: PortableTextComponentProps<ExtractedSection>
  index: number
  backgroundColor?: Color
  contentColor?: Color
}) => {
  const { value } = props.node
  const windowWidth = useWindowWidth()
  const currentVariant = useMemo(() => {
    if (windowWidth >= SCREEN_SIZE.XXL && value.xxl) {
      return value.xxl
    } else if (windowWidth >= SCREEN_SIZE.XL && value.xl) {
      return value.xl
    } else if (windowWidth >= SCREEN_SIZE.L && value.lg) {
      return value.lg
    } else if (windowWidth >= SCREEN_SIZE.M && value.md) {
      return value.md
    } else if (windowWidth >= SCREEN_SIZE.S && value.sm) {
      return value.sm
    }
    return value.base
  }, [windowWidth, value])

  const maxContentWidth = currentVariant.style.fullWidth
    ? windowWidth
    : currentVariant.style.maxContentWidth ?? MAX_CONTENT_WIDTH

  const backgroundImage = useMemo(() => {
    if (!currentVariant.style.backgroundImage) {
      return
    }
    return urlFor(currentVariant.style.backgroundImage)
      .width(maxContentWidth * 2)
      .url()
  }, [currentVariant.style.backgroundImage, maxContentWidth])

  if (windowWidth === 0) {
    return null
  }

  const { anchorWrapper } = multiColumnSection()

  return (
    <div className={anchorWrapper()} id={props.node.value.name}>
      <div
        className={twMerge(
          'flex w-full flex-col items-center',
          currentVariant.style.fullWidth && 'max-w-full',
          currentVariant.style.numberOfColumns > 1 &&
            currentVariant.style.columnGap === null &&
            'px-12',
          currentVariant.style.fullWidth &&
            bgColor({
              color: currentVariant.style.backgroundColor ?? undefined,
            }),
          currentVariant.style.fullWidth && backgroundImage && 'bg-cover bg-center bg-no-repeat',
          marginBottom({
            space: currentVariant.style.marginBottom ?? undefined,
          }),
          marginTop({
            space: currentVariant.style.marginTop ?? undefined,
          })
        )}
        style={{
          backgroundImage:
            currentVariant.style.fullWidth && backgroundImage
              ? `url(${backgroundImage})`
              : undefined,
        }}
      >
        <div
          className={twMerge(
            'relative grid w-full auto-rows-max overflow-hidden border-solid',
            bgColor({
              color: currentVariant.style.backgroundColor ?? undefined,
            }),
            borderColor({
              color: currentVariant.style.borderColor ?? undefined,
            }),
            backgroundImage && 'bg-cover bg-center bg-no-repeat',
            paddingX({ space: currentVariant.style.horizontalPadding ?? undefined }),
            paddingY({ space: currentVariant.style.verticalPadding ?? undefined }),
            justifyAlignmentTailwind[currentVariant.style.alignHorizontally ?? 'left'],
            itemsAlignmentTailwind[currentVariant.style.alignVertically ?? 'top'],
            borderRadius({
              radius: currentVariant.style.borderRadius ?? undefined,
            }),
            gapX({ space: currentVariant.style.columnGap ?? undefined }),
            gapY({ space: currentVariant.style.columnGap ?? undefined })
          )}
          style={{
            backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
            gridTemplateColumns:
              currentVariant.style.columnsEqualWidth === false && currentVariant.style.columnsLayout
                ? currentVariant.style.columnsLayout.map((width) => `${width}%`).join(' ')
                : '1fr',
            maxWidth: currentVariant.style.maxContentWidth ?? MAX_CONTENT_WIDTH,
          }}
        >
          {currentVariant.columns.map((column, index) => (
            <ColumnRenderer
              column={column}
              key={column._id}
              sectionName={value.name}
              maxContentWidth={currentVariant.style.maxContentWidth ?? MAX_CONTENT_WIDTH}
              backgroundColor={props.backgroundColor}
              contentColor={props.contentColor}
              layoutWidth={
                currentVariant.style.columnsLayout?.[index] &&
                currentVariant.style.columnsEqualWidth === false
                  ? currentVariant.style.columnsLayout[index]
                  : undefined
              }
              numberOfColumns={currentVariant.style.numberOfColumns}
              hasPriority={index < 2}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
